import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { useState, useEffect, useMemo } from "react";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import Table from "components/Table";
import InputField from "components/InputField";
import { BsCheckCircleFill, BsXCircleFill } from "react-icons/bs";
import { getEmployeeLeavesTypes, getLeaveTypes } from "api/apiHelper";
import { useNavigate } from "react-router-dom";


interface LeaveTypeType {
  id: string;
  name: string;
  description: string;
}

// Updated interface to match new backend response structure
interface LeaveAssignmentInfo {
  max_leaves_allowed: number;
  remaining_leaves: number;
}

// Updated to match the new backend structure
interface EmployeeLeaveData {
  emp_name: string;
  leave_details: {
    [leaveTypeName: string]: LeaveAssignmentInfo;
  };
}

interface AssignmentMatrixResponse {
  [employeeId: string]: EmployeeLeaveData;
}

interface AssignedLeavesTableData {
  employee_id: string;
  employee_name: string;
  [key: string]: any; // Dynamic properties for each leave type
}

const AssignedLeavesTable = () => {
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeType[]>([]);
  const [assignmentMatrix, setAssignmentMatrix] = useState<AssignmentMatrixResponse>({});
  const [tableData, setTableData] = useState<AssignedLeavesTableData[]>([]);
  const [filteredTableData, setFilteredTableData] = useState<AssignedLeavesTableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [allLeaveTypeNames, setAllLeaveTypeNames] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const navigate = useNavigate();

  // Fetch all required data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch leave types and assignment matrix
        const [leaveTypesResponse, matrixResponse] = await Promise.all([
          getLeaveTypes(),
          getEmployeeLeavesTypes() // This should call your assignment_matrix endpoint
        ]);

        console.log("Leave Types:", leaveTypesResponse.data);
        console.log("Assignment Matrix:", matrixResponse.data);

        setLeaveTypes(leaveTypesResponse.data);
        setAssignmentMatrix(matrixResponse.data);
        
        // Extract all unique leave type names from the matrix
        const uniqueLeaveNames = new Set<string>();
        Object.values(matrixResponse.data as AssignmentMatrixResponse).forEach(employeeData => {
          Object.keys(employeeData.leave_details).forEach(leaveName => {
            uniqueLeaveNames.add(leaveName);
          });
        });
        setAllLeaveTypeNames(Array.from(uniqueLeaveNames));
        
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Process the matrix data into table format
  useEffect(() => {
    if (Object.keys(assignmentMatrix).length > 0) {
      const processedData: AssignedLeavesTableData[] = Object.entries(assignmentMatrix).map(([employeeId, employeeData]) => {
        const row: AssignedLeavesTableData = {
          employee_id: employeeId,
          employee_name: employeeData.emp_name,
        };

        // For each leave type name, add the assignment info or null if not assigned
        allLeaveTypeNames.forEach(leaveName => {
          const leaveInfo = employeeData.leave_details[leaveName];
          row[`leave_type_${leaveName.replace(/\s+/g, '_')}`] = leaveInfo || null;
        });

        return row;
      });

      setTableData(processedData);
      setFilteredTableData(processedData);
    }
  }, [assignmentMatrix, allLeaveTypeNames]);

  // Filter table data based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTableData(tableData);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = tableData.filter((row) => {
      const employeeName = row.employee_name?.toLowerCase() || "";
      const employeeId = row.employee_id?.toLowerCase() || "";
      return employeeName.includes(searchLower) || employeeId.includes(searchLower);
    });

    setFilteredTableData(filtered);
  }, [searchTerm, tableData]);

  const columnHelper = createColumnHelper<AssignedLeavesTableData>();

  // Create columns dynamically
  const columns: ColumnDef<AssignedLeavesTableData, any>[] = [
    // Employee ID column
    columnHelper.accessor("employee_id", {
      id: "employee_id",
      header: () => <TableHeader>Employee ID</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="font-mono text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
            {info.getValue()}
          </span>
        </TableCell>
      ),
    }),
    
    // Employee name column
    columnHelper.accessor("employee_name", {
      id: "employee_name",
      header: () => <TableHeader>Employee Name</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span className="font-medium text-gray-900 dark:text-white">
            {info.getValue()}
          </span>
        </TableCell>
      ),
    }),
    
    // Dynamic leave type columns based on names from matrix
    ...allLeaveTypeNames.map(leaveName => {
      const columnKey = `leave_type_${leaveName.replace(/\s+/g, '_')}`;
      return columnHelper.accessor(columnKey, {
        id: columnKey,
        header: () => (
          <TableHeader>
            <div className="text-center">
              <div className="font-semibold">{leaveName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Remaining / Total
              </div>
            </div>
          </TableHeader>
        ),
        cell: (info) => {
          const leaveInfo: LeaveAssignmentInfo | null = info.getValue();
          const isAssigned = leaveInfo !== null && leaveInfo.max_leaves_allowed > 0;
          
          if (!isAssigned) {
            return (
              <TableCell>
                <div className="flex justify-center items-center gap-2">
                  <BsXCircleFill size={16} className="text-red-600 dark:text-red-400" />
                  <span className="text-gray-400">Not Assigned</span>
                </div>
              </TableCell>
            );
          }

          const remainingLeaves = leaveInfo.remaining_leaves;
          const maxLeaves = leaveInfo.max_leaves_allowed;
          const usedLeaves = maxLeaves - remainingLeaves;
          const utilizationPercentage = maxLeaves > 0 ? Math.round((usedLeaves / maxLeaves) * 100) : 0;
          
          // Color coding based on remaining leaves
          const getStatusColor = () => {
            if (remainingLeaves === 0) return "text-red-600 dark:text-red-400";
            if (remainingLeaves <= maxLeaves * 0.3) return "text-orange-600 dark:text-orange-400";
            return "text-green-600 dark:text-green-400";
          };

          const getBackgroundColor = () => {
            if (remainingLeaves === 0) return "bg-red-50 dark:bg-red-900/20";
            if (remainingLeaves <= maxLeaves * 0.3) return "bg-orange-50 dark:bg-orange-900/20";
            return "bg-green-50 dark:bg-green-900/20";
          };
          
          return (
            <TableCell>
              <div className="flex justify-center">
                <div className={`px-3 py-2 rounded-lg ${getBackgroundColor()}`}>
                  <div className="flex items-center gap-2 justify-center">
                    <BsCheckCircleFill size={16} className="text-green-600 dark:text-green-400" />
                    <div className="text-center">
                      <div className={`font-bold text-lg ${getStatusColor()}`}>
                        {remainingLeaves} / {maxLeaves}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {utilizationPercentage}% used
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TableCell>
          );
        }
      });
    }),
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading assignments...</span>
      </div>
    );
  }

  if (Object.keys(assignmentMatrix).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No employee assignment data available
      </div>
    );
  }

  // Calculate stats based on filtered data
  const filteredEmployeeIds = new Set(filteredTableData.map(row => row.employee_id));
  const filteredMatrix = Object.fromEntries(
    Object.entries(assignmentMatrix).filter(([empId]) => filteredEmployeeIds.has(empId))
  );

  const totalEmployees = filteredTableData.length;
  const totalAssignments = Object.values(filteredMatrix).reduce((total, employeeData) => {
    return total + Object.keys(employeeData.leave_details).length;
  }, 0);
  const totalPossibleAssignments = totalEmployees * allLeaveTypeNames.length;
  const coveragePercentage = totalPossibleAssignments > 0 
    ? Math.round((totalAssignments / totalPossibleAssignments) * 100) 
    : 0;

  // Calculate overall utilization statistics based on filtered data
  const overallStats = Object.values(filteredMatrix).reduce((stats, employeeData) => {
    Object.values(employeeData.leave_details).forEach(leaveInfo => {
      stats.totalMaxLeaves += leaveInfo.max_leaves_allowed;
      stats.totalRemainingLeaves += leaveInfo.remaining_leaves;
    });
    return stats;
  }, { totalMaxLeaves: 0, totalRemainingLeaves: 0 });

  const overallUtilization = overallStats.totalMaxLeaves > 0 
    ? Math.round(((overallStats.totalMaxLeaves - overallStats.totalRemainingLeaves) / overallStats.totalMaxLeaves) * 100)
    : 0;

  return (
    <div className="w-full">
      {/* Search Field */}
      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1 max-w-md">
          <InputField
            id="search"
            label="Search Employees"
            type="text"
            placeholder="Search by employee name or ID..."
            extra="w-full"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="!bg-white dark:!bg-gray-800"
          />
        </div>
        {searchTerm && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredTableData.length} of {tableData.length} employees
          </div>
        )}
      </div>

      <Table<AssignedLeavesTableData> 
        tableData={filteredTableData} 
        columns={columns} 
        tableHeading="Employee Leave Assignment Matrix" 
        onRowClick={(row) => navigate(`/leaves/employee/${row.employee_id}`)}
      />
      
      {/* Summary */}
      {filteredTableData.length > 0 && allLeaveTypeNames.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Employees: </span>
              <span className="font-medium">{totalEmployees}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Leave Types: </span>
              <span className="font-medium">{allLeaveTypeNames.length}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Total Assignments: </span>
              <span className="font-medium">{totalAssignments}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Coverage: </span>
              <span className="font-medium">{coveragePercentage}%</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Overall Utilization: </span>
              <span className="font-medium">{overallUtilization}%</span>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 text-sm">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Total Leaves Allocated: </span>
                <span className="font-medium">{overallStats.totalMaxLeaves}</span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Total Leaves Remaining: </span>
                <span className="font-medium">{overallStats.totalRemainingLeaves}</span>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AssignedLeavesTable;